import { decodeJWT, getPermissionsFromToken, getUserRoles } from './jwtDecoder';

/**
 * Comprehensive auth debug function that can be called from browser console
 * Usage: window.debugAuth()
 */
export const debugAuth = async () => {
  console.group('🚀 COMPREHENSIVE AUTH DEBUG REPORT');
  
  try {
    // Get Auth0 instance from window if available
    const auth0Client = (window as any).__auth0_client__;
    
    if (!auth0Client) {
      console.error('❌ Auth0 client not found on window object');
      console.groupEnd();
      return;
    }

    console.log('✅ Auth0 client found');
    
    // Check authentication status
    const isAuthenticated = await auth0Client.isAuthenticated();
    console.log('🔐 Is Authenticated:', isAuthenticated);
    
    if (!isAuthenticated) {
      console.warn('⚠️ User is not authenticated');
      console.groupEnd();
      return;
    }

    // Get user info
    const user = await auth0Client.getUser();
    console.log('👤 User Info:', user);

    // Get access token
    console.log('🔑 Requesting access token...');
    const token = await auth0Client.getTokenSilently({
      authorizationParams: {
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email read:games download:registrations'
      }
    });

    console.log('📄 Token Details:');
    console.log('  ├── Length:', token.length);
    console.log('  ├── First 50 chars:', token.substring(0, 50) + '...');
    console.log('  └── Last 50 chars:', '...' + token.substring(token.length - 50));

    // Decode token
    const decoded = decodeJWT(token);
    if (decoded) {
      console.log('🔍 Decoded Token Payload:');
      console.log('  ├── Subject:', decoded.sub);
      console.log('  ├── Audience:', decoded.aud);
      console.log('  ├── Issuer:', decoded.iss);
      console.log('  ├── Expires:', new Date(decoded.exp * 1000).toISOString());
      console.log('  ├── Issued At:', new Date(decoded.iat * 1000).toISOString());
      console.log('  └── All Claims:', decoded);
    }

    // Focus on TOKEN CLAIMS - roles and permissions analysis
    console.group('🎯 TOKEN ROLES & PERMISSIONS ANALYSIS');
    
    // Show raw token claims related to permissions/roles
    console.log('📄 Raw Token Claims (Auth-related only):');
    const authClaims: { [key: string]: any } = {};
    if (decoded) {
      Object.keys(decoded).forEach(key => {
        if (key.includes('role') || key.includes('permission') || key === 'scope' || key.includes('https://')) {
          authClaims[key] = decoded[key];
        }
      });
    }
    console.log(authClaims);

    // Extract permissions and roles with detailed breakdown
    console.group('🔍 DETAILED EXTRACTION PROCESS');
    
    // Check permissions from different sources
    console.log('📋 Permissions Sources:');
    if (decoded?.permissions && Array.isArray(decoded.permissions)) {
      console.log(`  ✅ permissions array: [${decoded.permissions.join(', ')}]`);
    } else {
      console.log('  ❌ permissions array: not found or not an array');
    }
    
    if (decoded?.scope && typeof decoded.scope === 'string') {
      const scopes = decoded.scope.split(' ').filter(Boolean);
      console.log(`  ✅ scope string: "${decoded.scope}" → [${scopes.join(', ')}]`);
    } else {
      console.log('  ❌ scope string: not found or not a string');
    }

    // Check roles from different sources
    console.log('👑 Roles Sources:');
    const possibleRoleKeys = [
      'https://games-admin-srq.pages.dev/roles',
      'https://coop-admin.example.com/roles',
      'https://example.com/roles',
      'roles'
    ];
    
    let rolesFound = false;
    possibleRoleKeys.forEach(key => {
      if (decoded?.[key] && Array.isArray(decoded[key])) {
        console.log(`  ✅ ${key}: [${decoded[key].join(', ')}]`);
        rolesFound = true;
      } else {
        console.log(`  ❌ ${key}: not found or not an array`);
      }
    });
    
    if (!rolesFound) {
      console.log('  ⚠️ No roles found in any expected location');
    }
    
    console.groupEnd();

    // Final extracted values
    const permissions = getPermissionsFromToken(token);
    const roles = getUserRoles(token);

    console.log('🎯 FINAL EXTRACTED VALUES:');
    console.log(`  📋 All Permissions: [${permissions.join(', ')}]`);
    console.log(`  👑 All Roles: [${roles.join(', ')}]`);

    // Check specific permissions for this app
    const requiredPermissions = ['read:games', 'download:registrations'];
    const requiredRoles = roles.filter(role => role.endsWith('_admin')); // Any admin role

    console.group('✅ COOP ADMIN ACCESS VALIDATION');
    
    console.log('📋 Required Permission Analysis:');
    requiredPermissions.forEach(permission => {
      const hasIt = permissions.includes(permission);
      const sources: string[] = [];
      
      // Check where this permission comes from
      if (decoded?.permissions && decoded.permissions.includes(permission)) {
        sources.push('permissions array');
      }
      if (decoded?.scope && decoded.scope.includes(permission)) {
        sources.push('scope string');
      }
      
      console.log(`  ${hasIt ? '✅' : '❌'} ${permission}${hasIt ? ` (from: ${sources.join(', ')})` : ''}`);
    });

    console.log('👤 Required Role Analysis:');
    requiredRoles.forEach(role => {
      const hasIt = roles.includes(role);
      const sources: string[] = [];
      
      // Check where this role comes from
      possibleRoleKeys.forEach(key => {
        if (decoded?.[key] && decoded[key].includes(role)) {
          sources.push(key);
        }
      });
      
      console.log(`  ${hasIt ? '✅' : '❌'} ${role}${hasIt ? ` (from: ${sources.join(', ')})` : ''}`);
    });

    // Final access determination
    const adminRoles = roles.filter(role => role.endsWith('_admin'));
    const isAdmin = adminRoles.length > 0;
    const canReadGames = permissions.includes('read:games') || isAdmin;
    const canDownload = permissions.includes('download:registrations') || isAdmin;

    console.log('🚦 FINAL ACCESS DECISION:');
    console.log(`  ├── Can Read Games: ${canReadGames ? '✅ YES' : '❌ NO'}`);
    console.log(`  ├── Can Download: ${canDownload ? '✅ YES' : '❌ NO'}`);
    console.log(`  ├── Admin Roles: [${adminRoles.join(', ')}]`);
    console.log(`  └── Is Any Admin: ${isAdmin ? '✅ YES' : '❌ NO'}`);

    console.groupEnd();
    console.groupEnd();

    // Environment variables check
    console.group('🌐 ENVIRONMENT CONFIGURATION');
    console.log('Auth0 Domain:', process.env.REACT_APP_AUTH0_DOMAIN);
    console.log('Auth0 Client ID:', process.env.REACT_APP_AUTH0_CLIENT_ID);
    console.log('Auth0 Audience:', process.env.REACT_APP_AUTH0_AUDIENCE);
    console.log('API Base URL:', process.env.REACT_APP_API_BASE_URL);
    console.groupEnd();

    console.log('✅ Auth debug report completed successfully');

  } catch (error) {
    console.error('❌ Error during auth debug:', error);
  }
  
  console.groupEnd();
};

/**
 * Simple debug function that only shows roles and permissions from token
 * Usage: window.debugTokenAuth()
 */
export const debugTokenAuth = async () => {
  console.group('🎯 TOKEN ROLES & PERMISSIONS ONLY');
  
  try {
    // Get Auth0 instance from window if available
    const auth0Client = (window as any).__auth0_client__;
    
    if (!auth0Client) {
      console.error('❌ Auth0 client not found. Make sure you are logged in.');
      console.groupEnd();
      return;
    }

    const isAuthenticated = await auth0Client.isAuthenticated();
    if (!isAuthenticated) {
      console.warn('⚠️ User is not authenticated');
      console.groupEnd();
      return;
    }

    // Get access token
    const token = await auth0Client.getTokenSilently({
      authorizationParams: {
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email read:games download:registrations'
      }
    });

    // Decode token
    const decoded = decodeJWT(token);
    if (!decoded) {
      console.error('❌ Could not decode token');
      console.groupEnd();
      return;
    }

    // Show ONLY auth-related claims
    console.log('📄 Auth-Related Token Claims:');
    const authClaims: { [key: string]: any } = {};
    Object.keys(decoded).forEach(key => {
      if (key.includes('role') || key.includes('permission') || key === 'scope' || key.includes('https://')) {
        authClaims[key] = decoded[key];
      }
    });
    console.table(authClaims);

    // Show what the app extracts
    const permissions = getPermissionsFromToken(token);
    const roles = getUserRoles(token);

    console.log('🔑 Extracted Permissions:', permissions);
    console.log('👑 Extracted Roles:', roles);

    // Show access status for this app
    const adminRoles = roles.filter(role => role.endsWith('_admin'));
    const isAdmin = adminRoles.length > 0;
    const canReadGames = permissions.includes('read:games') || isAdmin;
    const canDownload = permissions.includes('download:registrations') || isAdmin;

    console.log('🚦 Access Status:');
    console.log(`  📖 Can Read Games: ${canReadGames ? '✅' : '❌'}`);
    console.log(`  📥 Can Download: ${canDownload ? '✅' : '❌'}`);
    console.log(`  👑 Admin Roles: [${adminRoles.join(', ')}]`);
    console.log(`  🔑 Is Any Admin: ${isAdmin ? '✅' : '❌'}`);

  } catch (error) {
    console.error('❌ Error during token auth debug:', error);
  }
  
  console.groupEnd();
};

// Make debug functions available globally
if (typeof window !== 'undefined') {
  (window as any).debugAuth = debugAuth;
  (window as any).debugTokenAuth = debugTokenAuth;
  console.log('🔧 Debug functions available:');
  console.log('  - window.debugAuth() - Full auth debug report');
  console.log('  - window.debugTokenAuth() - Token roles & permissions only');
}
